/**
 * Exportação de tabelas do Agent/Prefab para CSV — 100% client-side, sem
 * backend. Separador `;` porque o Excel em pt-BR usa vírgula como decimal e
 * quebraria um CSV separado por vírgula.
 */
export const CSV_SEPARATOR = ";";

/** Excel só reconhece UTF-8 com BOM; sem isso, acentos viram mojibake. */
const BOM = "﻿";

/** Caracteres que fazem o Excel tratar a célula como fórmula. */
const FORMULA_TRIGGERS = ["=", "+", "@", "\t", "\r"];

function isNumericLike(value: string): boolean {
  return /^-?\d+([.,]\d+)?$/.test(value.trim());
}

export function formatCsvValue(value: unknown): string {
  if (value === null || value === undefined) return "";

  const raw = value instanceof Date ? value.toISOString() : String(value);

  // Neutraliza injeção de fórmula (=cmd|...), mantendo números negativos
  // legíveis — "-15" é um valor, não uma fórmula.
  const safe =
    FORMULA_TRIGGERS.some((t) => raw.startsWith(t)) ||
    (raw.startsWith("-") && !isNumericLike(raw))
      ? `'${raw}`
      : raw;

  const needsQuotes =
    safe.includes(CSV_SEPARATOR) ||
    safe.includes('"') ||
    safe.includes("\n") ||
    safe.includes("\r");

  return needsQuotes ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** União das chaves de todas as rows, para uma row esparsa não perder coluna. */
export function csvColumns(rows: Array<Record<string, unknown>>): string[] {
  const seen = new Set<string>();
  rows.forEach((row) => Object.keys(row ?? {}).forEach((k) => seen.add(k)));
  return [...seen];
}

export function rowsToCsv(rows: Array<Record<string, unknown>>): string {
  if (!rows?.length) return "";

  const columns = csvColumns(rows);

  const header = columns.map(formatCsvValue).join(CSV_SEPARATOR);
  const body = rows.map((row) =>
    columns.map((column) => formatCsvValue(row?.[column])).join(CSV_SEPARATOR),
  );

  return [header, ...body].join("\r\n");
}

/** conducco_insights_20260803T161500.csv */
export function buildCsvFilename(now: Date = new Date()): string {
  const timestamp = now.toISOString().replace(/[-:]/g, "").split(".")[0];
  return `conducco_insights_${timestamp}.csv`;
}

export function downloadCsv(
  rows: Array<Record<string, unknown>>,
  filename: string = buildCsvFilename(),
): void {
  const csv = rowsToCsv(rows);
  if (!csv) return;

  const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = "none";

  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  URL.revokeObjectURL(url);
}
